<template>
  <OperationFormLayoutSkeleton v-if="loading" />
  <OperationFormLayout
    v-if="!loading"
    :id="data?.id"
    :formDisabled="!imageValid"
    :formSaving="saving"
    operationType="heavy_mobile_equipment"
    @cancel="$router.back()"
    @update="save"
  >
    <!-- Details section -->
    <OperationFormSection
      :modifiers="['border-radius-top']"
      :sectionDescription="textData.details_description"
      :sectionTitle="textData.details_title"
    >
      <FormRow
        :errors="errors?.heavy_mobile_equipment?.name"
        :label="textData.equipment_name"
        labelFor="equipment_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div
            :class="{
              input: true,
              'input--error': errors?.heavy_mobile_equipment?.name,
            }"
          >
            <input
              id="equipment_name"
              :aria-label="textData.equipment_name"
              :placeholder="textData.equipment_name_placeholder"
              :value="data.name"
              @input="validateInput('name'); updateField($event.target.value, 'name')"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :label="textData.type"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <SelectList
            :labelGroup="labelGroup"
            :list="typeGroups"
            :modelValue="data.type"
            :modifiers="['side-panel-small']"
            @update:model-value="updateField($event, 'type')"
          />
        </div>
      </FormRow>
      <FormRow
        v-if="data?.type?.isNew"
        :errors="errors?.heavy_mobile_equipment?.heavy_mobile_equipment_type"
        :label="textData.new_type"
        labelFor="new_group_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div
            :class="{
              input: true,
              'input--error': errors?.heavy_mobile_equipment?.heavy_mobile_equipment_type,
            }"
          >
            <input
              id="new_group_name"
              :aria-label="textData.new_type"
              :placeholder="textData.new_type"
              :value="data.type.label"
              @input="validateInput('heavy_mobile_equipment_type'); addNewType($event)"
            >
          </div>
        </div>
      </FormRow>
    </OperationFormSection>

    <!-- Diagram section -->
    <NoticeMessageContainer v-if="data?.id && data?.diagram_thumbnail_url && warningText">
      <NoticeMessage
        colorStyle="warning"
        iconStyle="warning"
        :message="warningText"
        :modifiers="['narrow']"
      />
    </NoticeMessageContainer>
    <OperationFormSection
      :modifiers="['border-radius-bottom']"
      :sectionDescription="textData.equipment_diagram_description"
      :sectionTitle="textData.equipment_diagram_title"
    >
      <FormRow :modifiers="['no-padding']">
        <template v-if="data?.diagram_thumbnail_url">
          <img
            class="diagram-image"
            :src="data?.diagram_thumbnail_url"
          >
          <FormStandAloneErrorMessage
            v-if="!imageValid"
            :errorMessage="imageValidationMessage"
          />
          <div
            :class="{
              'form-layout__row--mobile form-layout__row--inline': data?.id,
              'form-layout__row--margin-top': true,
            }"
          >
            <div
              :class="{ 'form-layout__column form-layout__column--inline-button': data?.id, }"
            >
              <label
                class="diagram-button button"
                for="hme_change_diagram"
              >
                {{ textData.change_diagram }}
                <FileUploader
                  id="hme_change_diagram"
                  :acceptedFileTypes="acceptedFileTypes"
                  fileType="image"
                  :label="textData.add_image"
                  :modifiers="['hide']"
                  :multiple="false"
                  @update-blob="updateBlob"
                  @update-reader="updateReader"
                />
              </label>
            </div>
            <div
              v-if="data?.id"
              class="form-layout__column form-layout__column--inline-button"
            >
              <router-link
                class="button"
                :to="{ name: manageRegions }"
              >
                <ManageRegionSvg class="button__icon button--margin-right" />
                {{ textData.manage_regions }}
              </router-link>
            </div>
          </div>
        </template>
        <label
          v-else
          class="diagram-button button"
          for="hme_diagram"
        >
          {{ textData.upload_diagram }}
          <FileUploader
            id="hme_diagram"
            :acceptedFileTypes="acceptedFileTypes"
            fileType="image"
            :label="textData.add_image"
            :modifiers="['hide']"
            :multiple="false"
            @update-blob="updateBlob"
            @update-error="updateError"
            @update-reader="updateReader"
          />
        </label>
        <FormStandAloneErrorMessage
          v-if="!imageValid && !data?.diagram_thumbnail_url"
          :errorMessage="imageValidationMessage"
        />
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>
</template>

<script setup>
import { ref, computed } from 'vue';

// Utils
import I18n from 'i18n-js';
import { acceptedFileTypes } from '@/config/accepted_file_types';

// Components
import FormRow from '@/components/form/row.vue';
import SelectList from '@/components/shaftsV2/operations/select_list.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FileUploader from '@/components/generic/file_uploader.vue';
import NoticeMessageContainer from '@/components/generic/notice_message_container.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';

// Svg
import ManageRegionSvg from '@/images/manage-region.svg';

const props = defineProps({
  /** The current loading state */
  loading: { type: Boolean, required: true, default: false },
  /** The saving state of the form */
  saving: { type: Boolean, required: true, default: false },
  /** Sets the manage regions route */
  manageRegions: { type: String, required: true, default: null },
  /** List of existing types */
  typeGroups: { type: Array, required: true, default: () => [] },
  /** Hash of most text in the form */
  textData: { type: Object, required: true, default: null },
  /** The current form data, new or existing */
  data: { type: Object, required: true, default: null },
  /**
   * The error list after a failed save attempt,
   * sets the validation messages accordingly
   */
  errors: { type: Object, required: false, default: () => {} },
});

const emit = defineEmits([
  'save',
]);

const diagramUpload = ref();

const imageValid = ref(true);
const imageValidationMessage = ref(null);
const warningText = ref(null);

const labelGroup = computed(() => {
  return { newItemLabel: props.textData.new_type, noItemLabel: props.textData.no_type };
});

function validateInput(field) {
  if (props.errors?.heavy_mobile_equipment) {
    props.errors.heavy_mobile_equipment[field] = null;
  }
}

function updateBlob(file) {
  if (!file?.upload) return;
  diagramUpload.value = file.upload;
}

function updateReader(file) {
  if (!file?.url) return;

  props.data.diagram_thumbnail_url = file.url;
  warningText.value = I18n.t('general.warning_text');
}

function updateError(file) {
  if (!file?.filename) return;

  if (!acceptedFileTypes.includes(file.filename)) {
    imageValidationMessage.value = I18n.t('general.image_validation_message', {
      filename: file.filename,
    });
    imageValid.value = false;
  } else {
    imageValid.value = true;
  }
}

function save() {
  emit('save');
}

function updateField(value, type) {
  props.data[type] = value;
}

function addNewType(event) {
  props.data['type'] = { ...props.data.type, label: event.target.value };
}

defineExpose({diagramUpload});
</script>
